/**
 * The commit witness journal: what happened, in what order, and how it ended.
 *
 * A RECORD, NOT A CONTROL. Nothing in this module can start, retry, cancel or
 * undo an invocation, and nothing it renders navigates. The witness exists so
 * an operator watching an agent work can see the committed action after the
 * fact, which is a different job from approving one, and the standing project
 * decision is that there is no confirmation dialog to approve into.
 *
 * Bounded on purpose. An agent can invoke tools in a loop, and an unbounded
 * array behind a fixed overlay is a leak that only shows up on a long session.
 */
import type { Consequence, Diff } from '../contract';

/** How an invocation ended. `pending` is the only non-terminal member. */
export type WitnessOutcome = 'pending' | 'ok' | 'refused' | 'cancelled';

/**
 * Who invoked. `agent` is Chrome calling a registered tool; `human` is an
 * operator pressing Invoke in the tool panel, which takes the same code path
 * through `executeTool` and is therefore distinguishable only because the
 * panel says so when it publishes the start.
 */
export type WitnessSource = 'agent' | 'human';

export interface WitnessRow {
  readonly id: string;
  readonly toolName: string;
  /** Verb first, the way the card reads it. Never the model-facing description. */
  readonly sentence: string;
  /** Null when the tool is registered by someone whose catalog we do not hold. */
  readonly consequence: Consequence | null;
  readonly source: WitnessSource;
  readonly startedAt: number;
  /**
   * Present only when a publisher supplied it. Chrome's `toolactivated` carries
   * a tool name and nothing else, so an agent invocation shows no diff rows
   * until the runtime publishes one.
   */
  readonly diff: Diff | null;
  readonly outcome: WitnessOutcome;
  readonly refusal: string | null;
  readonly settledAt: number | null;
}

/**
 * How many invocations the journal keeps. Older rows are dropped from the tail,
 * so the newest is always index 0.
 */
export const JOURNAL_LIMIT = 24;

/** How many of them the overlay draws. The rest stay readable in the journal. */
export const VISIBLE_LIMIT = 4;

export interface OpenInput {
  readonly id: string;
  readonly toolName: string;
  readonly sentence: string;
  readonly consequence: Consequence | null;
  readonly source: WitnessSource;
  readonly startedAt: number;
  readonly diff?: Diff | null;
}

export function openRow(rows: readonly WitnessRow[], input: OpenInput): WitnessRow[] {
  const row: WitnessRow = {
    id: input.id,
    toolName: input.toolName,
    sentence: input.sentence,
    consequence: input.consequence,
    source: input.source,
    startedAt: input.startedAt,
    diff: input.diff ?? null,
    outcome: 'pending',
    refusal: null,
    settledAt: null,
  };
  return [row, ...rows].slice(0, JOURNAL_LIMIT);
}

export interface SettleInput {
  readonly outcome: Exclude<WitnessOutcome, 'pending'>;
  readonly diff?: Diff | null;
  readonly refusal?: string | null;
  readonly settledAt: number;
}

/**
 * Settles a row by id, or by tool name when no id is known.
 *
 * NAME MATCHING TAKES THE NEWEST PENDING ROW AND ONLY A PENDING ROW. Chrome's
 * `toolcancel` names a tool and not an invocation, so two concurrent calls to
 * the same tool are genuinely ambiguous. Settling the newest pending one is the
 * only choice that cannot resurrect an already terminal row, and a terminal row
 * being wrong is worse than a pending one being attributed to its sibling.
 */
export function settleRow(
  rows: readonly WitnessRow[],
  match: { readonly id?: string; readonly toolName?: string },
  input: SettleInput,
): WitnessRow[] {
  const index = rows.findIndex((row) =>
    match.id !== undefined
      ? row.id === match.id
      : row.outcome === 'pending' && row.toolName === match.toolName,
  );
  if (index < 0) return rows as WitnessRow[];

  const found = rows[index];
  if (!found) return rows as WitnessRow[];

  const settled: WitnessRow = {
    ...found,
    outcome: input.outcome,
    diff: input.diff ?? found.diff,
    refusal: input.refusal ?? found.refusal,
    settledAt: input.settledAt,
  };
  const next = rows.slice();
  next[index] = settled;
  return next;
}

/**
 * The card's own label. Uppercase because the Generated UI frame is.
 *
 * A read that succeeded committed nothing, so it says so. An unknown
 * consequence still reads as a committed action, which is the fail-closed
 * direction: the label that overstates what happened is the safe one.
 */
export function rowLabel(row: WitnessRow): string {
  switch (row.outcome) {
    case 'pending':
      return 'PENDING';
    case 'refused':
      return 'REFUSED';
    case 'cancelled':
      return 'CANCELLED';
    case 'ok':
      return row.consequence === 'reads' ? 'READ' : 'COMMITTED ACTION';
    default:
      return 'COMMITTED ACTION';
  }
}
